import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ModelPicker, AgentPromptLink } from './modelInfo';
import { backdropClose } from '../backdrop';
import ImageModal from '../ImageModal';

// Illustrations batch: per point, Claude agents search the web for pictures that
// explain the text, LOOK at every candidate (the editor's own MCP tools — no
// shell on the server), a second agent re-checks each accepted one, a text-only
// pass moves spoilers to "after the answer", captions are checked against the
// forbidden stems, and one eye-level view of the stop is picked.
// Everything lands in a run directory on the server as it happens, so this
// panel shows three levels: the batch, every point (phase + counters), and in
// the point modal every candidate with its verdict trail and the live trace.
// The preview page is built from the SAME manifest the load inserts.

const COLOR = '#c2185b';
// One link per agent of the chain, in running order (served by /api/pipeline/prompt-file/:key).
const PROMPTS = [
  { label: 'Search', file: 'illus_search' },
  { label: 'Editor', file: 'illus_editor' },
  { label: 'Spoilers', file: 'illus_spoiler' },
  { label: 'Captions', file: 'illus_captionfix' },
  { label: 'View of the stop', file: 'illus_look' },
];
const SECTION_LABEL = { desc: 'Description', task: 'Task', after: 'After answer', look: 'View of the stop' };

async function api(path, { method = 'GET', body } = {}) {
  const res = await fetch(`/api${path}`, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch (e) { /* empty body */ }
  if (!res.ok) throw new Error((data && data.error) || `HTTP ${res.status}`);
  return data;
}

function fmtDur(ms) {
  if (ms == null) return '…';
  const total = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h) return `${h}h ${String(m).padStart(2, '0')}m`;
  return m ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
}

const STATUS_CHIP = {
  queued: { bg: '#9aa0a6', label: 'queued' },
  running: { bg: COLOR, label: 'running' },
  waiting: { bg: '#5f6368', label: '⏸ limit wait' },
  done: { bg: '#188038', label: '✓ done' },
  error: { bg: '#c5221f', label: 'error' },
  stopped: { bg: '#5f6368', label: 'stopped' },
  interrupted: { bg: '#5f6368', label: 'interrupted' },
};
const UNFINISHED = ['queued', 'running', 'waiting', 'interrupted', 'error', 'stopped'];

const fileUrl = (questId, id, thumb) => `/api/pipeline/quest/${encodeURIComponent(questId)}/illustrations-file/${encodeURIComponent(id)}${thumb ? '?thumb=1' : ''}`;

// Full diagnostics stay visible, folded — never just the first line.
function Errors({ errors }) {
  if (!errors || !errors.length) return null;
  return (
    <div style={{ color: '#c5221f', fontSize: 12, marginTop: 4 }}>
      {errors[0]}
      {errors.length > 1 && (
        <details style={{ marginTop: 2 }}>
          <summary style={{ cursor: 'pointer' }}>details</summary>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, maxHeight: 300, overflow: 'auto', background: 'rgba(0,0,0,0.04)', padding: 6 }}>{errors.slice(1).join('\n\n')}</pre>
        </details>
      )}
    </div>
  );
}

function Counters({ c, s, look, stems }) {
  const cell = { whiteSpace: 'nowrap', color: '#5f6368' };
  return (
    <>
      <span style={cell} title="downloaded · accepted / rejected by search · kept / dropped by editor">
        {c ? `⬇${c.fetched} · ✓${c.accepted} ✗${c.rejected}${c.kept || c.dropped ? ` · ed ${c.kept}/${c.dropped}` : ''}` : '—'}
      </span>
      <span style={cell} title="kept pictures: Description / Task / After answer">
        {s ? `D${s.by_section.desc} T${s.by_section.task} A${s.by_section.after}` : ''}
      </span>
      <span style={cell} title="view of the stop">{look === 'chosen' ? '👁 ✓' : look === 'none' ? '👁 —' : ''}</span>
      {stems && stems.status === 'hits_left' && <span style={{ color: '#b25000' }} title="some captions still contain forbidden stems">⚠ stems</span>}
      {stems && stems.status === 'unavailable' && <span style={{ color: '#b25000' }} title="the stems list is not configured on the server (ILLUS_STEMS_FILE)">⚠ no stem check</span>}
    </>
  );
}

// ---- point modal ------------------------------------------------------------------------------
function PointModal({ questId, runId, point, running, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [trace, setTrace] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [tab, setTab] = useState('pictures');
  // Full-size viewer over the modal: { images: [{url, caption}], index }
  const [viewer, setViewer] = useState(null);

  const reload = useCallback(async () => {
    try { setData(await api(`/pipeline/quest/${questId}/illustrations/${point.tag}`)); setError(null); } catch (e) { setError(e.message); }
  }, [questId, point.tag]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
  }, [running, reload]);

  // Live trace of this point's agents (same SSE channel the other panels use).
  useEffect(() => {
    if (!runId) return undefined;
    const es = new EventSource(`/api/pipeline/runs/${runId}/events`);
    const keep = (ev) => ev && (ev.t === 'status' || ev.t === 'tool');
    es.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch (err) { return; }
      if (msg.type === 'pipeline_replay' && msg.pointUuid === point.uuid && msg.node === 'illustrations') {
        setTrace((msg.trace || []).filter(keep).slice(-300));
      } else if (msg.type === 'pipeline_trace' && msg.ev && msg.ev.pointUuid === point.uuid && keep(msg.ev)) {
        setTrace((t) => [...t.slice(-299), msg.ev]);
      }
    };
    return () => es.close();
  }, [runId, point.uuid]);

  const patch = async (body) => {
    try {
      const r = await api(`/pipeline/quest/${questId}/illustrations/${point.tag}/item`, { method: 'PATCH', body });
      setData((d) => ({ ...d, out: r.out }));
      if (onChanged) onChanged();
    } catch (e) { setError(e.message); }
  };

  const out = data && data.out;
  const items = (out && out.items) || [];
  const records = (data && data.records) || [];
  const phases = (data && data.phases) || [];
  const tabBtn = (k, label) => (
    <button key={k} onClick={() => setTab(k)} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px 10px', fontWeight: tab === k ? 700 : 400, color: tab === k ? COLOR : '#5f6368', borderBottom: tab === k ? `2px solid ${COLOR}` : '2px solid transparent' }}>{label}</button>
  );

  // A plain render function, not a component: a component declared in here would
  // get a new identity on every keystroke and remount, dropping the textarea focus.
  const renderItem = (it) => {
    const dropped = !it.kept || (it.human && it.human.drop);
    const caption = (it.human && it.human.caption != null) ? it.human.caption : it.caption_ru;
    const draft = drafts[it.id];
    const source = (it.human && it.human.source != null) ? it.human.source : [it.author_or_site, it.license && !/^unknown$/i.test(it.license) ? it.license : ''].filter(Boolean).join(', ');
    const srcKey = `${it.id}#src`;
    const srcDraft = drafts[srcKey];
    return (
      <div key={it.id} style={{ display: 'flex', gap: 10, padding: 8, borderRadius: 8, background: 'rgba(0,0,0,0.03)', opacity: dropped ? 0.5 : 1 }}>
        <img src={fileUrl(questId, it.id, true)} alt="" onClick={() => openPicture(it.id)} title="Open full size" style={{ width: 160, height: 120, objectFit: 'cover', borderRadius: 6, flexShrink: 0, cursor: 'zoom-in' }} />
        <div style={{ flex: 1, minWidth: 0, fontSize: 12 }}>
          <textarea
            value={draft != null ? draft : caption}
            onChange={(e) => setDrafts((d) => ({ ...d, [it.id]: e.target.value }))}
            rows={2}
            style={{ width: '100%', fontSize: 13, boxSizing: 'border-box' }}
          />
          {draft != null && draft !== caption && (
            <button onClick={() => { patch({ id: it.id, caption: draft }); setDrafts((d) => { const n = { ...d }; delete n[it.id]; return n; }); }} style={{ fontSize: 11 }}>Save caption</button>
          )}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}>
            <span style={{ color: '#5f6368', whiteSpace: 'nowrap' }}>Источник:</span>
            <input value={srcDraft != null ? srcDraft : source} onChange={(e) => setDrafts((d) => ({ ...d, [srcKey]: e.target.value }))} style={{ flex: 1, fontSize: 12 }} />
            {srcDraft != null && srcDraft !== source && (
              <button onClick={() => { patch({ id: it.id, source: srcDraft }); setDrafts((d) => { const n = { ...d }; delete n[srcKey]; return n; }); }} style={{ fontSize: 11 }}>Save</button>
            )}
          </div>
          <div style={{ color: '#5f6368', marginTop: 4 }}><b>Seen:</b> {it.what_i_see}</div>
          {it.illustrates && <div style={{ color: '#5f6368' }}><b>Illustrates:</b> {it.illustrates}</div>}
          {it.identity_check && it.identity_check !== 'n/a' && <div style={{ color: '#5f6368' }}><b>Identity:</b> {it.identity_check}</div>}
          {it.editor && <div style={{ color: it.kept ? '#188038' : '#c5221f' }}><b>Editor {it.editor.verdict}:</b> {it.editor.note}{it.editor.drop_reason ? ` — ${it.editor.drop_reason}` : ''}</div>}
          {it.spoiler_moved_from && <div style={{ color: '#b25000' }}>Moved from {SECTION_LABEL[it.spoiler_moved_from]} (spoiler): {it.spoiler_reason}</div>}
          {it.stem_hits ? <div style={{ color: '#b25000' }}>⚠ caption or source line contains a forbidden stem — reword it</div> : null}
          <div style={{ marginTop: 4, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {it.source_page_url && <a href={it.source_page_url} target="_blank" rel="noreferrer">source page</a>}
            <span style={{ color: '#5f6368' }}>{[it.width && `${it.width}×${it.height}`, it.image_url && <a key="u" href={it.image_url} target="_blank" rel="noreferrer">file url</a>].filter(Boolean).map((x, i) => <React.Fragment key={i}>{i ? ' · ' : ''}{x}</React.Fragment>)}</span>
            {it.kept && (
              <button style={{ marginLeft: 'auto', fontSize: 11 }} onClick={() => patch({ id: it.id, drop: !(it.human && it.human.drop) })}>
                {it.human && it.human.drop ? '↺ Return' : '✕ Drop'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const look = out && out.look;
  // Pictures tab: one gallery in the order shown (view of the stop, then the
  // three sections), so ←/→ walks through the point like the page does.
  const openPicture = (id) => {
    const list = [];
    if (look && look.chosen) list.push({ id: look.chosen.id, caption: `${SECTION_LABEL.look}: ${look.chosen.what_i_see || ''}` });
    for (const sec of ['desc', 'task', 'after']) {
      for (const it of items.filter((x) => x.section === sec)) {
        const cap = (it.human && it.human.caption != null) ? it.human.caption : it.caption_ru;
        list.push({ id: it.id, caption: `${SECTION_LABEL[sec]}: ${cap || ''}` });
      }
    }
    const index = Math.max(0, list.findIndex((x) => x.id === id));
    setViewer({ images: list.map((x) => ({ url: fileUrl(questId, x.id), caption: x.caption })), index });
  };
  // Verdicts tab: every candidate in the order it was judged, with the verdict.
  const openTrail = (i) => setViewer({
    images: records.map((r) => ({ url: fileUrl(questId, r.id), caption: `${r.phase} · ${r.verdict}${r.section ? ` [${r.section}]` : ''}: ${r.what_i_see || ''}${r.reason ? ` — ${r.reason}` : ''}` })),
    index: i,
  });
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} {...backdropClose(onClose)}>
      <div style={{ background: 'var(--bg,#fff)', borderRadius: 10, padding: 16, width: 900, maxWidth: '96vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h3 style={{ margin: 0, flex: 1 }}>{point.n}. {point.name}</h3>
          <span style={{ fontSize: 12, color: '#5f6368' }}>phase: {(out && out.phase) || '—'}</span>
          <button onClick={onClose}>Close</button>
        </div>
        <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border,#eee)', margin: '8px 0' }}>
          {tabBtn('pictures', `Pictures (${items.filter((i) => i.kept && !(i.human && i.human.drop)).length}/${items.length})`)}
          {tabBtn('trail', `Verdicts (${records.length})`)}
          {tabBtn('phases', `Phases (${phases.length})`)}
          {tabBtn('trace', 'Live trace')}
        </div>
        {error && <div style={{ color: '#c5221f', fontSize: 12 }}>{error}</div>}
        <div style={{ overflow: 'auto', flex: 1 }}>
          {tab === 'pictures' && (
            <>
              {!out && <div style={{ color: '#5f6368', fontSize: 13 }}>No results yet.</div>}
              {out && (
                <>
                  <h4 style={{ margin: '6px 0' }}>{SECTION_LABEL.look}</h4>
                  {!look && <div style={{ fontSize: 12, color: '#5f6368' }}>Not searched yet.</div>}
                  {look && look.chosen && (
                    <div style={{ display: 'flex', gap: 10, fontSize: 12, opacity: look.human && look.human.drop ? 0.5 : 1 }}>
                      <img src={fileUrl(questId, look.chosen.id, true)} alt="" onClick={() => openPicture(look.chosen.id)} title="Open full size" style={{ width: 200, height: 140, objectFit: 'cover', borderRadius: 6, cursor: 'zoom-in' }} />
                      <div style={{ flex: 1 }}>
                        <div><b>Seen:</b> {look.chosen.what_i_see}</div>
                        <div><b>Confirmed by {look.chosen.confirm}:</b> {look.chosen.confirm_detail}</div>
                        <div><b>Answer hidden:</b> {look.chosen.answer_hidden}</div>
                        {look.chosen.source_page_url && <a href={look.chosen.source_page_url} target="_blank" rel="noreferrer">source page</a>}
                        <div><button style={{ fontSize: 11, marginTop: 4 }} onClick={() => patch({ id: 'look', drop: !(look.human && look.human.drop) })}>{look.human && look.human.drop ? '↺ Return' : '✕ Drop'}</button></div>
                      </div>
                    </div>
                  )}
                  {look && !look.chosen && <div style={{ fontSize: 12, color: '#b25000' }}>Not confirmed — shoot on site: {look.shoot_on_site || '—'}</div>}
                  {['desc', 'task', 'after'].map((sec) => {
                    const list = items.filter((i) => i.section === sec);
                    return (
                      <div key={sec}>
                        <h4 style={{ margin: '12px 0 6px' }}>{SECTION_LABEL[sec]} ({list.filter((i) => i.kept && !(i.human && i.human.drop)).length})</h4>
                        <div style={{ display: 'grid', gap: 8 }}>{list.map(renderItem)}</div>
                      </div>
                    );
                  })}
                  {out.not_found && out.not_found.length > 0 && (
                    <details style={{ marginTop: 10, fontSize: 12 }}><summary>Not found ({out.not_found.length})</summary><ul>{out.not_found.map((x, i) => <li key={i}>{x}</li>)}</ul></details>
                  )}
                </>
              )}
            </>
          )}
          {tab === 'trail' && (
            <div style={{ display: 'grid', gap: 4 }}>
              {records.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, fontSize: 12, alignItems: 'flex-start' }}>
                  <img src={fileUrl(questId, r.id, true)} alt="" onClick={() => openTrail(i)} title="Open full size" style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 4, flexShrink: 0, cursor: 'zoom-in' }} />
                  <span style={{ whiteSpace: 'nowrap', color: '#5f6368' }}>{(r.ts || '').slice(11, 19)} {r.phase}</span>
                  <span style={{ whiteSpace: 'nowrap', fontWeight: 600, color: /accepted|kept/.test(r.verdict) ? '#188038' : '#c5221f' }}>{r.verdict}</span>
                  <span style={{ flex: 1 }}>{r.section ? `[${r.section}] ` : ''}{r.what_i_see || ''}{r.reason ? ` — ${r.reason}` : ''}</span>
                </div>
              ))}
              {!records.length && <div style={{ color: '#5f6368', fontSize: 13 }}>No verdicts recorded yet.</div>}
            </div>
          )}
          {tab === 'phases' && (
            <div style={{ display: 'grid', gap: 6, fontSize: 12 }}>
              {phases.map((p, i) => (
                <div key={i}>
                  <span style={{ color: p.ok ? '#188038' : '#c5221f', fontWeight: 600 }}>{p.ok ? '✓' : '✗'} {p.phase}</span>
                  <span style={{ color: '#5f6368' }}> · {(p.ts || '').slice(11, 19)} · {fmtDur(p.ms)}{p.cost != null ? ` · $${Number(p.cost).toFixed(2)}` : ''}</span>
                  {data.inputs && data.inputs[p.phase] && (
                    <details><summary style={{ cursor: 'pointer', fontSize: 11 }}>input (what the agent got)</summary><pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, maxHeight: 300, overflow: 'auto', background: 'rgba(0,0,0,0.04)', padding: 6 }}>{data.inputs[p.phase]}</pre></details>
                  )}
                  <Errors errors={p.errors} />
                  {p.raw && <details><summary style={{ cursor: 'pointer', fontSize: 11 }}>raw answer</summary><pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, maxHeight: 240, overflow: 'auto' }}>{p.raw}</pre></details>}
                </div>
              ))}
              {!phases.length && <div style={{ color: '#5f6368' }}>No finished phases yet.</div>}
            </div>
          )}
          {tab === 'trace' && (
            <div style={{ fontFamily: 'monospace', fontSize: 11, whiteSpace: 'pre-wrap' }}>
              {trace.map((ev, i) => (
                <div key={i} style={{ color: ev.t === 'tool' ? '#5f6368' : 'inherit' }}>
                  {new Date(ev.ts).toLocaleTimeString()} {ev.t === 'tool' ? `→ ${ev.name} ${ev.detail || ''}` : ev.msg}
                </div>
              ))}
              {!trace.length && <div style={{ color: '#5f6368' }}>Nothing yet (the trace lives in memory until the editor restarts).</div>}
            </div>
          )}
        </div>
      </div>
      {/* The editor's own gallery viewer (←/→, Esc, caption) above this modal. */}
      {viewer && <ImageModal images={viewer.images} startIndex={viewer.index} onClose={() => setViewer(null)} />}
    </div>
  );
}

// ---- panel ------------------------------------------------------------------------------------
const IllustrationsBatchPanel = ({ questId, runId, locations, embedded, onStatus }) => {
  const [overview, setOverview] = useState(null);
  const [batch, setBatch] = useState(null);
  const [loadState, setLoadState] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showScope, setShowScope] = useState(false);
  const [selected, setSelected] = useState({});
  const [openPoint, setOpenPoint] = useState(null);

  const refresh = useCallback(async () => {
    if (!questId) return;
    try {
      const o = await api(`/pipeline/quest/${questId}/illustrations`);
      setOverview(o);
      setBatch(o.batch);
      setLoadState(o.load);
    } catch (e) { setError(e.message); }
  }, [questId]);
  useEffect(() => { refresh(); }, [refresh]);

  const running = !!(batch && (batch.status === 'running' || batch.status === 'waiting_limit'));
  const loading = !!(loadState && loadState.status === 'running');

  useEffect(() => {
    if (!(running || loading) || !runId) return undefined;
    const es = new EventSource(`/api/pipeline/runs/${runId}/events`);
    es.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch (err) { return; }
      if (msg.type === 'illustrations_batch') setBatch(msg.batch);
      else if (msg.type === 'illustrations_batch_done') { setBatch(msg.batch); refresh(); }
      else if (msg.type === 'illustrations_load') { setLoadState(msg.load); if (msg.load.status !== 'running') refresh(); }
    };
    return () => es.close();
  }, [running, loading, runId, refresh]);

  const done = batch ? batch.done_count || 0 : 0;
  const total = batch ? batch.total || 0 : 0;
  useEffect(() => {
    if (onStatus) onStatus({ running, done, total, status: batch ? batch.status : null, hasBatch: !!batch });
  }, [running, done, total, batch, onStatus]);

  // One row per quest location: live batch info when present, else what is on disk.
  const batchByUuid = new Map(((batch && batch.points) || []).map((bp) => [bp.uuid, bp]));
  const rows = ((overview && overview.points) || []).map((p) => {
    const bp = batchByUuid.get(p.uuid);
    return { ...p, status: bp ? bp.status : (p.phase === 'done' ? 'done' : null), phase: (bp && bp.status === 'running' && bp.phase) || p.phase || (bp && bp.phase), counters: (bp && bp.counters) || p.counters, summary: (bp && bp.summary) || p.summary, look: (bp && bp.look) || p.look, errors: bp && bp.errors, resume_at: bp && bp.resume_at };
  });
  const notDone = rows.filter((r) => r.phase !== 'done').map((r) => r.uuid);
  const allUuids = rows.map((r) => r.uuid);
  const selectedUuids = allUuids.filter((u) => selected[u]);

  const start = async (uuids) => {
    setError(null); setBusy(true);
    try {
      await api(`/pipeline/quest/${questId}/illustrations-batch`, { method: 'POST', body: { uuids } });
      setShowScope(false);
      await refresh();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const stop = async () => { try { await api(`/pipeline/quest/${questId}/illustrations-batch/stop`, { method: 'POST', body: {} }); } catch (e) { setError(e.message); } };
  const clear = async () => {
    if (!window.confirm('Delete the batch progress record? All pictures and verdicts stay on the server.')) return;
    try { await api(`/pipeline/quest/${questId}/illustrations-batch/clear`, { method: 'POST', body: {} }); await refresh(); } catch (e) { setError(e.message); }
  };
  const runLoad = async (commit) => {
    if (commit && !window.confirm('Insert every picture of the preview page into the images table (test quest) in one transaction?')) return;
    setError(null);
    try { await api(`/pipeline/quest/${questId}/illustrations/load`, { method: 'POST', body: { commit } }); setLoadState({ status: 'running', commit, done: 0, total: 0 }); } catch (e) { setError(e.message); }
  };

  const primaryBtn = { background: COLOR, color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 4, cursor: 'pointer', fontWeight: 600 };
  const stopBtn = { background: '#c5221f', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 4, cursor: 'pointer' };
  const pct = total ? Math.round((done / total) * 100) : 0;
  const elapsed = batch && batch.started_at ? Date.now() - new Date(batch.started_at).getTime() : null;

  return (
    <div style={embedded ? { padding: '4px 2px 2px' } : { border: '1px solid var(--border,#ddd)', borderRadius: 8, padding: '10px 12px', margin: '8px 8px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {!running && <button style={primaryBtn} disabled={busy || !rows.length} onClick={() => { setSelected(Object.fromEntries(notDone.map((u) => [u, true]))); setShowScope(true); }}>Illustrate…{notDone.length ? ` (${notDone.length} not done)` : ''}</button>}
        {running && <button style={stopBtn} onClick={stop}>■ Stop (kills running agents, keeps finished phases)</button>}
        {running && batch.status === 'waiting_limit' && <span style={{ fontSize: 12, color: COLOR }}>⏸ usage limit — retry {batch.resume_at ? new Date(batch.resume_at).toLocaleTimeString() : ''}</span>}
        <ModelPicker k="illustrations" />
        <a href={`/api/pipeline/quest/${encodeURIComponent(questId)}/illustrations-preview`} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>Preview page ↗</a>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: '#5f6368' }}>
          {running && <span>elapsed {fmtDur(elapsed)} · ETA {fmtDur(batch.eta_ms)}{batch.avg_point_ms ? ` · ~${fmtDur(batch.avg_point_ms)}/point` : ''}</span>}
          {!running && batch && <button className="remove-location-button" onClick={clear} title="Delete the progress record — results stay">×</button>}
        </span>
      </div>
      {error && <div style={{ color: '#c5221f', fontSize: 12, marginTop: 6 }}>{error}</div>}
      {batch && batch.errors && <Errors errors={batch.errors} />}

      {batch && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, margin: '8px 0 4px' }}>
          <div style={{ flex: 1, height: 8, background: 'rgba(0,0,0,0.08)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: running ? COLOR : '#188038' }} />
          </div>
          <span style={{ whiteSpace: 'nowrap', color: '#5f6368' }}>{done} / {total}{batch.error_count ? ` · ${batch.error_count} failed` : ''}{!running ? ` · ${batch.status}` : ` · ${batch.lanes} lane(s)`}</span>
        </div>
      )}

      <div style={{ maxHeight: 280, overflow: 'auto', border: '1px solid var(--border,#eee)', borderRadius: 6, padding: 4, marginTop: 6 }}>
        {rows.map((r) => {
          const chip = r.status ? STATUS_CHIP[r.status] || STATUS_CHIP.queued : null;
          return (
            <div key={r.uuid} onClick={() => setOpenPoint(r)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 6px', fontSize: 12, cursor: 'pointer', borderRadius: 4, background: r.status === 'running' ? 'rgba(194,24,91,0.08)' : 'transparent' }}>
              <span style={{ width: 22, color: '#5f6368' }}>{r.n}</span>
              {chip ? <span style={{ background: chip.bg, color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 10, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{chip.label}</span> : <span style={{ width: 50 }} />}
              {r.status === 'running' && r.phase && <span style={{ color: COLOR, whiteSpace: 'nowrap' }}>{r.phase}</span>}
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
              <Counters c={r.counters} s={r.summary} look={r.look} stems={r.stems} />
              {r.errors && <span title={r.errors[0]} style={{ color: '#c5221f' }}>⚠</span>}
            </div>
          );
        })}
      </div>

      {!running && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 8, fontSize: 12 }}>
          <button onClick={() => runLoad(false)} disabled={loading}>Dry run load</button>
          <button style={primaryBtn} onClick={() => runLoad(true)} disabled={loading} title="Inserts the pictures of the preview page that are not in images yet; earlier loads of this run are skipped">Load to images</button>
          {overview && overview.loaded && <span style={{ color: '#188038' }} title={(overview.loaded.loads || []).map((l) => `${l.at.slice(0, 16).replace('T', ' ')} ${l.by}: ${l.rows}`).join('\n')}>in images: {overview.loaded.inserted.length} rows ({(overview.loaded.loads || [overview.loaded]).length} load(s), last {overview.loaded.at.slice(0, 16).replace('T', ' ')} by {overview.loaded.by})</span>}
          {loadState && (
            <span style={{ color: loadState.status === 'error' ? '#c5221f' : '#5f6368' }}>
              {loadState.commit ? 'load' : 'dry run'}: {loadState.status}{loadState.status === 'running' ? ` ${loadState.done}/${loadState.total}` : ''}
              {loadState.summary ? ` · ${loadState.summary.rows} new rows${loadState.summary.skipped_already_loaded ? ` (${loadState.summary.skipped_already_loaded} already loaded, skipped)` : ''}, ${(loadState.summary.bytes / 1048576).toFixed(1)} MB${loadState.summary.ids ? `, ids ${loadState.summary.ids[0]}–${loadState.summary.ids[1]}` : ''}` : ''}
              {loadState.summary && loadState.summary.test_stand ? ` · test stand: ${loadState.summary.test_stand.missing.length ? `${loadState.summary.test_stand.missing.length} missing!` : 'all visible'}` : ''}
            </span>
          )}
        </div>
      )}
      {loadState && loadState.errors && <Errors errors={loadState.errors} />}
      {loadState && loadState.summary && loadState.summary.warnings && loadState.summary.warnings.length > 0 && (
        <div style={{ fontSize: 12, color: '#b25000' }}>{loadState.summary.warnings.join('; ')}</div>
      )}

      {showScope && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} {...backdropClose(() => setShowScope(false))}>
          <div style={{ background: 'var(--bg,#fff)', borderRadius: 10, padding: 20, width: 540, maxWidth: '92vw' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>Illustrate <ModelPicker k="illustrations" /></h3>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 12, marginBottom: 8 }}>
              <span style={{ opacity: 0.7 }}>Prompts:</span>
              {PROMPTS.map((p) => <AgentPromptLink key={p.file} entry={p} text={p.label} style={{ fontSize: 12 }} />)}
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              <button onClick={() => setSelected(Object.fromEntries(notDone.map((u) => [u, true])))}>Not done ({notDone.length})</button>
              <button onClick={() => setSelected(Object.fromEntries(allUuids.map((u) => [u, true])))}>All ({allUuids.length})</button>
              <button onClick={() => setSelected({})} style={{ marginLeft: 'auto' }}>Clear</button>
            </div>
            <div style={{ maxHeight: 260, overflow: 'auto', border: '1px solid var(--border,#eee)', borderRadius: 6, padding: 6 }}>
              {rows.map((r) => (
                <label key={r.uuid} style={{ display: 'flex', gap: 8, fontSize: 13, padding: '3px 2px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!selected[r.uuid]} onChange={(e) => setSelected((s) => ({ ...s, [r.uuid]: e.target.checked }))} />
                  <span style={{ flex: 1 }}>{r.n}. {r.name}</span>
                  {r.phase && <span style={{ fontSize: 10, color: r.phase === 'done' ? '#188038' : COLOR }}>{r.phase === 'done' ? '✓ done' : `stopped at ${r.phase}`}</span>}
                </label>
              ))}
            </div>
            <div style={{ fontSize: 12, color: COLOR, background: 'rgba(194,24,91,0.06)', borderRadius: 6, padding: '6px 8px', marginTop: 8 }}>
              Per point: search (downloads and looks at every candidate) → editor re-checks each picture → a second search for an empty section → spoiler check → caption stem check → one view of the stop. Results are kept on the server; a point re-run resumes from its last finished phase. Nothing goes to the images table until you press “Load to images”. Usually 15–40 minutes per point.
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowScope(false)}>Cancel</button>
              <button style={primaryBtn} disabled={busy || !selectedUuids.length} onClick={() => start(selectedUuids)}>Start ({selectedUuids.length})</button>
            </div>
          </div>
        </div>
      )}

      {openPoint && (
        <PointModal
          questId={questId}
          runId={runId}
          point={openPoint}
          running={!!(running && batchByUuid.get(openPoint.uuid) && batchByUuid.get(openPoint.uuid).status === 'running')}
          onClose={() => setOpenPoint(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
};

export default IllustrationsBatchPanel;
